import type {
  CreateLookerReportRequest,
  LookerDataset,
  LookerReportGranularity,
  PeriodicReport,
} from '@brand24-original/llm-types';
import { client } from '../apiClient';

const LOOKER_STUDIO_ENDPOINT = '/looker-studio';
const PERIODIC_REPORTS_ENDPOINT = '/periodic-reports';

export class LookerStudioService {
  static async connect(authCode: string): Promise<boolean> {
    try {
      const response = await client(true, true).post(`${LOOKER_STUDIO_ENDPOINT}/connect`, { authCode });
      return response.status === 200;
    } catch (error) {
      console.error('Error connecting to Looker Studio:', error);
      return false;
    }
  }

  /**
   * Creates a Looker Studio export: the backend builds the sheet (one tab per dataset) and fills it in
   * the background; the report starts PENDING until the user pastes the saved report link.
   */
  static async createReport(input: {
    projectId: number;
    granularity: LookerReportGranularity;
    datasets: LookerDataset[];
    reportName: string;
  }): Promise<PeriodicReport> {
    const body: CreateLookerReportRequest = {
      projectId: input.projectId,
      provider: 'LOOKER_STUDIO',
      type: input.granularity,
      config: { parameters: { reportName: input.reportName, datasets: input.datasets } },
    };
    const response = await client(true, true).post(`${PERIODIC_REPORTS_ENDPOINT}/create`, body);
    return response.data.data;
  }

  static async getConnectionStatus(): Promise<{ isConnected: boolean; reportId?: string }> {
    const response = await client(true, true).get(`${LOOKER_STUDIO_ENDPOINT}/connection-status`);
    return response.data;
  }

  static async disconnect(): Promise<boolean> {
    try {
      const response = await client(true, true).post(`${LOOKER_STUDIO_ENDPOINT}/disconnect`);
      return response.status === 200;
    } catch (error) {
      console.error('Error disconnecting:', error);
      return false;
    }
  }

  static async getReportUrl(reportId: number): Promise<{ url: string; status: 'create' | 'exists' }> {
    const response = await client(true, true).post(`${LOOKER_STUDIO_ENDPOINT}/get-report-url`, { reportId });
    return response.data;
  }

  static async saveFinalReportUrl(reportId: number, finalReportUrl: string): Promise<boolean> {
    try {
      const response = await client(true, true).post(`${LOOKER_STUDIO_ENDPOINT}/save-final-url`, { 
        reportId, 
        finalReportUrl 
      });
      return response.status === 200;
    } catch (error) {
      console.error('Error saving final report URL:', error);
      return false;
    }
  }
}
